import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../contexts/AuthContext'
import { useAlerts } from '../../contexts/AlertContext'
import { usePermissionCheck } from '../../contexts/PermissionContext'
import { useWorkspace } from '../../contexts/WorkspaceContext'
import PermissionGuard, { EditButton, DeleteButton } from '../../components/permissions/PermissionGuard'
import apiService from '../../services/api'
import {
  HiOutlineVideoCamera,
  HiOutlinePlus,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineMagnifyingGlass,
  HiOutlineArrowDownTray,
  HiOutlineChevronLeft,
  HiOutlineChevronRight,
  HiOutlineUser,
  HiOutlineCalendarDays,
  HiOutlineClock,
  HiOutlineDocument,
  HiOutlineXMark,
  HiOutlineFunnel,
  HiOutlineChevronDoubleLeft,
  HiOutlineChevronDoubleRight,
  HiOutlineFolder,
} from 'react-icons/hi2'
import ConfirmDialog from '../../components/ui/ConfirmDialog'
import {
  Badge,
  Button,
  Card,
  Checkbox,
  EmptyState,
  FormField,
  IconButton,
  Input,
  PageHeader,
  Select,
  Spinner,
} from '../../components/ui'

export default function SessionsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { user, isWorkspaceSwitched, ownerAccountId } = useAuth()
  const { showSuccess, showError } = useAlerts()
  const { canCreate } = usePermissionCheck()
  const { getEntity } = useWorkspace()

  const [sessions, setSessions] = useState([])
  const [pageLoading, setPageLoading] = useState(true)
  const [sessionsLoading, setSessionsLoading] = useState(false)
  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '')
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '')
  const [isSearching, setIsSearching] = useState(false)
  const [selectedSessions, setSelectedSessions] = useState([])
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false, title: '', message: '', onConfirm: null, type: 'warning',
  })

  const [filters, setFilters] = useState({
    title:       searchParams.get('title') || '',
    case:        searchParams.get('case') || '',
    client:      searchParams.get('client') || '',
    date_after:  searchParams.get('date_after') || '',
    date_before: searchParams.get('date_before') || '',
  })
  const [sortBy, setSortBy] = useState(searchParams.get('ordering') || '-date')
  const [showFilters, setShowFilters] = useState(false)

  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page')) || 1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [itemsPerPage, setItemsPerPage] = useState(parseInt(searchParams.get('page_size')) || 20)

  useEffect(() => {
    const id = setTimeout(() => {
      if (searchInput !== searchQuery) {
        setSearchQuery(searchInput); setCurrentPage(1)
      }
      setIsSearching(false)
    }, 500)
    if (searchInput !== searchQuery && searchInput.length > 0) setIsSearching(true)
    return () => clearTimeout(id)
  }, [searchInput, searchQuery])

  useEffect(() => {
    const isFirstLoad = pageLoading && sessions.length === 0
    fetchSessions(isFirstLoad)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, searchQuery, filters, sortBy, itemsPerPage, ownerAccountId])

  useEffect(() => {
    const params = new URLSearchParams()
    if (searchQuery) params.set('search', searchQuery)
    if (filters.title) params.set('title', filters.title)
    if (filters.case) params.set('case', filters.case)
    if (filters.client) params.set('client', filters.client)
    if (filters.date_after) params.set('date_after', filters.date_after)
    if (filters.date_before) params.set('date_before', filters.date_before)
    if (sortBy !== '-date') params.set('ordering', sortBy)
    if (currentPage > 1) params.set('page', currentPage)
    if (itemsPerPage !== 20) params.set('page_size', itemsPerPage)
    setSearchParams(params)
  }, [searchQuery, filters, sortBy, currentPage, itemsPerPage, setSearchParams])

  async function fetchSessions(isInitialLoad = false) {
    try {
      if (isInitialLoad) setPageLoading(true); else setSessionsLoading(true)
      const params = { page: currentPage, page_size: itemsPerPage }
      if (searchQuery) params.search = searchQuery
      if (filters.title) params.title = filters.title
      if (filters.case) params.case = filters.case
      if (filters.client) params.client = filters.client
      if (filters.date_after) params.date_after = filters.date_after
      if (filters.date_before) params.date_before = filters.date_before
      if (sortBy) params.ordering = sortBy
      const response = await apiService.get('/sessions/', { params })
      if (response.success) {
        const data = response.data.data || response.data
        const arr = data.data || data.results || []
        const pagination = data.pagination || {}
        setSessions(arr)
        setTotalCount(pagination.count || arr.length)
        setTotalPages(pagination.total_pages || Math.ceil((pagination.count || arr.length) / itemsPerPage))
      } else {
        showError(response.error?.message || `Failed to load ${getEntity('session').plural.toLowerCase()}`)
      }
    } catch {
      showError(`Failed to load ${getEntity('session').plural.toLowerCase()}`)
    } finally {
      if (isInitialLoad) setPageLoading(false); else setSessionsLoading(false)
    }
  }

  async function handleDeleteSession(sessionId) {
    try {
      setConfirmDialog({ isOpen: false })
      const params = isWorkspaceSwitched && ownerAccountId ? { owner_account_id: ownerAccountId } : {}
      const response = await apiService.delete(`/sessions/${sessionId}/`, { params })
      if (response.success) {
        setSessions(prev => prev.filter(s => s.id !== sessionId))
        setSelectedSessions(prev => prev.filter(id => id !== sessionId))
        showSuccess(`${getEntity('session').singular} deleted successfully`)
      } else {
        showError(response.error?.message || `Failed to delete ${getEntity('session').singular.toLowerCase()}`)
      }
    } catch {
      showError(`Failed to delete ${getEntity('session').singular.toLowerCase()}`)
    }
  }

  const confirmDeleteSession = (session) => {
    setConfirmDialog({
      isOpen: true,
      title: `Delete ${getEntity('session').singular}`,
      message: `Are you sure you want to delete "${session.title}"? This cannot be undone and will remove all associated ${getEntity('document').plural.toLowerCase()}.`,
      type: 'danger',
      confirmText: `Delete ${getEntity('session').singular}`,
      onConfirm: () => handleDeleteSession(session.id),
    })
  }

  const handleBulkDelete = () => {
    if (selectedSessions.length === 0) return
    setConfirmDialog({
      isOpen: true,
      title: `Delete ${selectedSessions.length} ${getEntity('session').plural}`,
      message: `Are you sure you want to delete ${selectedSessions.length} ${selectedSessions.length === 1 ? getEntity('session').singular.toLowerCase() : getEntity('session').plural.toLowerCase()}? This cannot be undone.`,
      type: 'danger',
      confirmText: `Delete ${selectedSessions.length}`,
      onConfirm: async () => {
        setConfirmDialog({ isOpen: false })
        try {
          await Promise.all(selectedSessions.map(sessionId => {
            const params = isWorkspaceSwitched && ownerAccountId ? { owner_account_id: ownerAccountId } : {}
            return apiService.delete(`/sessions/${sessionId}/`, { params })
          }))
          setSessions(prev => prev.filter(s => !selectedSessions.includes(s.id)))
          setSelectedSessions([])
          showSuccess(`${selectedSessions.length} ${selectedSessions.length === 1 ? getEntity('session').singular.toLowerCase() : getEntity('session').plural.toLowerCase()} deleted`)
        } catch {
          showError(`Failed to delete some ${getEntity('session').plural.toLowerCase()}`)
        }
      },
    })
  }

  const toggleSelect = (id) => setSelectedSessions(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])
  const toggleSelectAll = () => setSelectedSessions(selectedSessions.length === sessions.length ? [] : sessions.map(s => s.id))

  const clearFilters = () => {
    setFilters({ title: '', case: '', client: '', date_after: '', date_before: '' })
    setSearchInput(''); setSearchQuery(''); setSortBy('-date'); setCurrentPage(1)
  }

  const hasActiveFilters = !!(searchQuery || filters.title || filters.case || filters.client || filters.date_after || filters.date_before)
  const activeFilterCount = (searchQuery ? 1 : 0) + Object.values(filters).filter(Boolean).length

  const formatDate = (s) => new Date(s).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
  const formatTime = (timeString) => {
    if (!timeString) return ''
    try {
      return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('en-US', {
        hour: 'numeric', minute: '2-digit', hour12: true,
      })
    } catch { return timeString }
  }

  if (pageLoading) {
    return (
      <div className="space-y-6">
        <div className="space-y-2">
          <div className="h-6 w-1/3 bg-muted rounded animate-pulse" />
          <div className="h-4 w-1/2 bg-muted rounded animate-pulse" />
        </div>
        <Card padding="md">
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-20 bg-muted rounded-xl animate-pulse" />
            ))}
          </div>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <PageHeader
        icon={HiOutlineVideoCamera}
        iconTone="brand"
        title={t('sessions.title', { entity: getEntity('session').plural })}
        subtitle={isWorkspaceSwitched
          ? t('sessions.subtitleSwitched', { entity: getEntity('session').plural.toLowerCase(), name: user?.switchedAccountName })
          : t('sessions.subtitle', { entity: getEntity('session').plural.toLowerCase(), caseEntity: getEntity('case').singular.toLowerCase() })}
        actions={
          <>
            <PermissionGuard resource="session" action="read">
              <Button variant="secondary" leadingIcon={HiOutlineArrowDownTray} onClick={() => { /* TODO */ }}>
                {t('common.export')}
              </Button>
            </PermissionGuard>
            <PermissionGuard resource="session" action="create">
              <Button leadingIcon={HiOutlinePlus} onClick={() => navigate('/sessions/create')}>
                {t('sessions.addSession', { entity: getEntity('session').singular })}
              </Button>
            </PermissionGuard>
          </>
        }
      />

      <Card padding="none">
        <div className="flex flex-col lg:flex-row lg:items-center gap-3 p-4 sm:p-5 border-b border-border-subtle">
          <div className="flex-1 max-w-2xl">
            <Input
              leadingIcon={HiOutlineMagnifyingGlass}
              placeholder={t('sessions.searchPlaceholder', { entity: getEntity('session').plural.toLowerCase(), caseEntity: getEntity('case').singular.toLowerCase() })}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              trailingAction={
                isSearching ? <Spinner size="sm" className="me-2" />
                : searchInput ? (
                  <IconButton
                    icon={HiOutlineXMark}
                    aria-label={t('common.clearSearch')}
                    variant="ghost"
                    size="sm"
                    onClick={() => { setSearchInput(''); setSearchQuery('') }}
                  />
                ) : null
              }
            />
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <Select
              value={sortBy}
              onChange={(e) => { setSortBy(e.target.value); setCurrentPage(1) }}
              className="min-w-[10rem]"
            >
              <option value="-date">{t('sort.dateDesc')}</option>
              <option value="date">{t('sort.dateAsc')}</option>
              <option value="-created_at">{t('sort.newest')}</option>
              <option value="created_at">{t('sort.oldest')}</option>
              <option value="title">{t('sort.titleAsc')}</option>
              <option value="-title">{t('sort.titleDesc')}</option>
            </Select>

            <Button
              variant={showFilters || hasActiveFilters ? 'secondary' : 'ghost'}
              leadingIcon={HiOutlineFunnel}
              onClick={() => setShowFilters(v => !v)}
            >
              {t('common.filters')}
              {hasActiveFilters && <Badge tone="brand" size="sm" className="ms-1">{activeFilterCount}</Badge>}
            </Button>
          </div>

          {selectedSessions.length > 0 && (
            <div className="flex items-center gap-2 ps-3 border-s border-border-subtle">
              <Badge tone="brand">{t('common.selected', { count: selectedSessions.length })}</Badge>
              <PermissionGuard resource="session" action="delete">
                <Button variant="danger" size="sm" leadingIcon={HiOutlineTrash} onClick={handleBulkDelete}>
                  {t('common.delete')}
                </Button>
              </PermissionGuard>
            </div>
          )}
        </div>

        {showFilters && (
          <div className="p-4 sm:p-5 bg-muted/40 border-b border-border-subtle">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
              <FormField label="Title">
                <Input value={filters.title}
                  onChange={(e) => { setFilters(f => ({ ...f, title: e.target.value })); setCurrentPage(1) }}
                  placeholder="Filter by title…" />
              </FormField>
              <FormField label={getEntity('case').singular}>
                <Input value={filters.case}
                  onChange={(e) => { setFilters(f => ({ ...f, case: e.target.value })); setCurrentPage(1) }}
                  placeholder={`Filter by ${getEntity('case').singular.toLowerCase()}…`} />
              </FormField>
              <FormField label={getEntity('client').singular}>
                <Input value={filters.client}
                  onChange={(e) => { setFilters(f => ({ ...f, client: e.target.value })); setCurrentPage(1) }}
                  placeholder={`Filter by ${getEntity('client').singular.toLowerCase()}…`} />
              </FormField>
              <FormField label="Date from">
                <Input type="date" value={filters.date_after}
                  onChange={(e) => { setFilters(f => ({ ...f, date_after: e.target.value })); setCurrentPage(1) }} />
              </FormField>
              <FormField label="Date to">
                <Input type="date" value={filters.date_before}
                  onChange={(e) => { setFilters(f => ({ ...f, date_before: e.target.value })); setCurrentPage(1) }} />
              </FormField>
            </div>
            {hasActiveFilters && (
              <div className="flex items-center justify-between mt-3">
                <span className="text-xs text-ink-muted">{totalCount} result{totalCount !== 1 ? 's' : ''}</span>
                <Button variant="link" size="sm" onClick={clearFilters}>Clear all filters</Button>
              </div>
            )}
          </div>
        )}

        <div className="relative">
          {sessionsLoading && (
            <div className="absolute inset-0 bg-surface/70 backdrop-blur-sm flex items-center justify-center z-10 rounded-b-xl">
              <div className="inline-flex items-center gap-2">
                <Spinner size="sm" />
                <span className="text-sm text-ink-secondary">Loading…</span>
              </div>
            </div>
          )}

          {sessions.length === 0 ? (
            <EmptyState
              icon={HiOutlineVideoCamera}
              title={t('sessions.noFound', { entity: getEntity('session').plural.toLowerCase() })}
              description={
                searchQuery
                  ? t('sessions.noMatchSearch', { entity: getEntity('session').plural.toLowerCase(), query: searchQuery })
                  : canCreate('session')
                    ? t('sessions.getStarted', { entity: getEntity('session').singular.toLowerCase() })
                    : t('sessions.noAvailable', { entity: getEntity('session').plural.toLowerCase() })
              }
              action={
                !searchQuery && (
                  <PermissionGuard resource="session" action="create">
                    <Button leadingIcon={HiOutlinePlus} onClick={() => navigate('/sessions/create')}>
                      {t('sessions.createFirst', { entity: getEntity('session').singular.toLowerCase() })}
                    </Button>
                  </PermissionGuard>
                )
              }
            />
          ) : (
            <>
              <div className="px-4 sm:px-5 py-2.5 border-b border-border-subtle bg-muted/40">
                <Checkbox
                  checked={selectedSessions.length === sessions.length && sessions.length > 0}
                  onChange={toggleSelectAll}
                  disabled={sessionsLoading}
                >
                  {t('cases.selectAll', {
                    count: sessions.length,
                    entity: sessions.length !== 1 ? getEntity('session').plural.toLowerCase() : getEntity('session').singular.toLowerCase(),
                  })}
                </Checkbox>
              </div>

              <div className={`p-4 sm:p-5 transition-opacity ${sessionsLoading ? 'opacity-50' : ''}`}>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                  {sessions.map((session) => (
                    <SessionCard
                      key={session.id}
                      session={session}
                      selected={selectedSessions.includes(session.id)}
                      onToggle={() => toggleSelect(session.id)}
                      onView={() => navigate(`/sessions/${session.id}`)}
                      onEdit={() => navigate(`/sessions/${session.id}/edit`)}
                      onDelete={() => confirmDeleteSession(session)}
                      formatDate={formatDate}
                      formatTime={formatTime}
                      getEntity={getEntity}
                      disabled={sessionsLoading}
                    />
                  ))}
                </div>
              </div>

              <div className={`flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 p-4 sm:p-5 border-t border-border-subtle bg-muted/30 transition-opacity ${sessionsLoading ? 'opacity-50' : ''}`}>
                <div className="flex items-center gap-3 flex-wrap">
                  <p className="text-sm text-ink-muted">
                    {t('pagination.showing')}{' '}
                    <span className="font-semibold text-ink-primary">
                      {sessions.length === 0 ? 0 : ((currentPage - 1) * itemsPerPage) + 1}
                    </span>{' '}
                    –{' '}
                    <span className="font-semibold text-ink-primary">
                      {Math.min(currentPage * itemsPerPage, totalCount)}
                    </span>{' '}
                    {t('pagination.of')} <span className="font-semibold text-ink-primary">{totalCount}</span>
                  </p>
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-ink-muted">{t('pagination.show')}:</span>
                    <Select
                      size="sm"
                      value={itemsPerPage}
                      onChange={(e) => { setItemsPerPage(parseInt(e.target.value)); setCurrentPage(1) }}
                      disabled={sessionsLoading}
                      className="w-auto"
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </Select>
                  </div>
                </div>

                {totalPages > 1 && (
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    disabled={sessionsLoading}
                    onChange={setCurrentPage}
                  />
                )}
              </div>
            </>
          )}
        </div>
      </Card>

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type}
        confirmText={confirmDialog.confirmText}
      />
    </div>
  )
}

function SessionCard({ session, selected, onToggle, onView, onEdit, onDelete, formatDate, formatTime, getEntity, disabled }) {
  const { t } = useTranslation()
  return (
    <div className="bg-surface border border-border rounded-xl p-4 hover:border-border-strong hover:shadow-token-sm transition-all">
      <div className="flex items-start gap-3 mb-3">
        <div className="pt-1">
          <Checkbox checked={selected} onChange={onToggle} disabled={disabled} aria-label={t('common.selectAll')} />
        </div>
        <span className="w-10 h-10 bg-brand-soft text-brand rounded-xl flex items-center justify-center flex-shrink-0">
          <HiOutlineVideoCamera className="w-5 h-5" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <h3 className="text-base font-semibold text-ink-primary truncate">{session.title}</h3>
            {session.is_active != null && (
              <Badge tone={session.is_active ? 'success' : 'neutral'} size="sm">
                {session.is_active ? t('common.active') : t('common.inactive')}
              </Badge>
            )}
          </div>
        </div>
      </div>

      <dl className="space-y-1.5 text-sm text-ink-secondary mb-3">
        <div className="flex items-center gap-2">
          <HiOutlineFolder className="w-4 h-4 text-ink-muted" />
          <span className="truncate">{session.case_title || session.case?.title || t('sessions.unknownCase', { entity: getEntity('case').singular })}</span>
        </div>
        <div className="flex items-center gap-2">
          <HiOutlineUser className="w-4 h-4 text-ink-muted" />
          <span className="truncate">{session.client_name || session.client?.name || t('sessions.unknownClient', { entity: getEntity('client').singular })}</span>
        </div>
        <div className="flex items-center gap-2">
          <HiOutlineCalendarDays className="w-4 h-4 text-ink-muted" />
          <span>{formatDate(session.date)}</span>
        </div>
        {session.ttime && (
          <div className="flex items-center gap-2">
            <HiOutlineClock className="w-4 h-4 text-ink-muted" />
            <span>{formatTime(session.ttime)}</span>
          </div>
        )}
        {session.description && <p className="text-ink-muted line-clamp-2">{session.description}</p>}
        <div className="flex items-center gap-2">
          <HiOutlineDocument className="w-4 h-4 text-ink-muted" />
          <span>{t('sessions.groupCount', { count: session.document_groups_count || 0, entity: getEntity('document').singular.toLowerCase() })}</span>
        </div>
      </dl>

      <div className="flex items-center justify-between pt-3 border-t border-border-subtle">
        <Button variant="link" size="sm" onClick={onView} disabled={disabled}>
          {t('common.viewDetails')}
        </Button>
        <div className="flex items-center gap-1">
          <EditButton resource="session">
            <IconButton
              icon={HiOutlinePencil}
              aria-label={t('common.edit')}
              variant="ghost"
              size="sm"
              onClick={onEdit}
              disabled={disabled}
            />
          </EditButton>
          <DeleteButton resource="session">
            <IconButton
              icon={HiOutlineTrash}
              aria-label={t('common.delete')}
              variant="danger"
              size="sm"
              onClick={onDelete}
              disabled={disabled}
            />
          </DeleteButton>
        </div>
      </div>
    </div>
  )
}

function Pagination({ currentPage, totalPages, disabled, onChange }) {
  const { t } = useTranslation()
  const maxVisible = 5
  let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2))
  let endPage = Math.min(totalPages, startPage + maxVisible - 1)
  if (endPage - startPage + 1 < maxVisible) startPage = Math.max(1, endPage - maxVisible + 1)
  const pages = []
  for (let i = startPage; i <= endPage; i++) pages.push(i)

  return (
    <div className="flex items-center gap-1">
      <IconButton icon={HiOutlineChevronDoubleLeft} aria-label={t('pagination.firstPage')} variant="ghost" size="sm" className="rtl:rotate-180" disabled={currentPage === 1 || disabled} onClick={() => onChange(1)} />
      <IconButton icon={HiOutlineChevronLeft} aria-label={t('pagination.previousPage')} variant="ghost" size="sm" className="rtl:rotate-180" disabled={currentPage === 1 || disabled} onClick={() => onChange(Math.max(1, currentPage - 1))} />
      {pages.map(i => (
        <button
          key={i}
          type="button"
          onClick={() => onChange(i)}
          disabled={disabled}
          className={`min-w-[2rem] h-8 px-2 text-sm font-medium rounded-md transition-colors disabled:opacity-50 ${
            i === currentPage ? 'bg-brand text-white' : 'text-ink-secondary hover:bg-muted'
          }`}
        >
          {i}
        </button>
      ))}
      {currentPage < totalPages - 2 && (
        <>
          <span className="px-1 text-ink-muted text-sm">…</span>
          <button
            type="button"
            onClick={() => onChange(totalPages)}
            disabled={disabled}
            className="min-w-[2rem] h-8 px-2 text-sm font-medium text-ink-secondary hover:bg-muted rounded-md disabled:opacity-50"
          >
            {totalPages}
          </button>
        </>
      )}
      <IconButton icon={HiOutlineChevronRight} aria-label={t('pagination.nextPage')} variant="ghost" size="sm" className="rtl:rotate-180" disabled={currentPage === totalPages || disabled} onClick={() => onChange(Math.min(totalPages, currentPage + 1))} />
      <IconButton icon={HiOutlineChevronDoubleRight} aria-label={t('pagination.lastPage')} variant="ghost" size="sm" className="rtl:rotate-180" disabled={currentPage === totalPages || disabled} onClick={() => onChange(totalPages)} />
    </div>
  )
}
